import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Grade } from '../types';

interface GradeCardProps {
  grade: Grade;
  educationLevel: 'primary' | 'middle' | 'high' | 'university';
}

const GradeCard = ({ grade, educationLevel }: GradeCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const typeLabel = (t: string) => {
    switch (t) {
      case 'regular':
        return 'Notă';
      case 'conduct':
        return 'Purtare';
      case 'thesis':
        return 'Teză';
      case 'lab':
        return 'Laborator';
      case 'seminar':
        return 'Seminar';
      case 'homework':
        return 'Temă';
      case 'final':
        return 'Final';
      case 'exam':
        return 'Examen';
      case 'project':
        return 'Proiect';
      case 'assignment':
        return 'Tema';
      case 'participation':
        return 'Participare';
      default:
        return t;
    }
  };

  const getGradeColor = (gradeValue: number | string) => {
    if (educationLevel === 'primary') {
      const qualifications = ['FB', 'B', 'S', 'I'];
      const index = qualifications.indexOf(gradeValue as string);
      if (index === 0) return 'text-error';
      if (index === 1) return 'text-warning';
      if (index === 2) return 'text-success';
      if (index === 3) return 'text-primary';
      return 'text-muted-foreground';
    }

    if (typeof gradeValue === 'number') {
      if (gradeValue >= 8) return 'text-success';
      if (gradeValue >= 6) return 'text-primary';
      if (gradeValue >= 4) return 'text-warning';
      return 'text-error';
    }

    return 'text-foreground';
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'regular':
        return 'Award';
      case 'conduct':
        return 'ShieldCheck';
      case 'thesis':
        return 'GraduationCap';
      case 'lab':
        return 'FlaskConical';
      case 'seminar':
        return 'Presentation';
      case 'homework':
        return 'BookOpen';
      case 'final':
        return 'Trophy';
      case 'exam':
        return 'FileText';
      case 'project':
        return 'Briefcase';
      case 'assignment':
        return 'BookOpen';
      case 'participation':
        return 'Users';
      default:
        return 'Award';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-all duration-200">
      <div
        className="cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <Icon name={getTypeIcon(grade.type)} size={20} className="text-muted-foreground" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground">{grade.subject}</h4>
              <p className="text-xs text-muted-foreground">{typeLabel(grade.type)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-2xl font-bold ${getGradeColor(grade.grade)}`}>
              {grade.grade}
            </span>
            <Icon
              name="ChevronDown"
              size={16}
              className={`text-muted-foreground transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Icon name="Calendar" size={12} />
            <span>{new Date(grade.date).toLocaleDateString('ro-RO', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="User" size={12} />
            <span>{grade.teacher}</span>
          </div>
          {grade.weight && (
            <div className="flex items-center gap-1">
              <Icon name="TrendingUp" size={12} />
              <span>{grade.weight}% pondere</span>
            </div>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-border space-y-2 animate-scale-in">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Descriere</p>
            <p className="text-sm text-foreground">{(grade.description || '').toString().trim() || '—'}</p>
          </div>
          {grade.feedback && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Feedback profesor</p>
              <p className="text-sm text-foreground">{grade.feedback}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GradeCard;
